import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  DndContext,
  useDroppable,
  useDraggable,
  type DragEndEvent,
} from '@dnd-kit/core';
import type {
  CategorizationBlock,
  CategorizationCategory,
  CategorizationIcon,
  CategorizationItem,
} from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import {
  createRichTextRendererExtensions,
  createRichTextContentResetStyles,
  safeGenerateNonEmptyRichTextHTML,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { getStandardInteractionWidth } from './interactionLayout';
import { useDefaultDndSensors } from './hooks/useDefaultDndSensors';
import { useCommittedQuestionNarrationOutcome } from './hooks/useCommittedQuestionNarrationOutcome';
import { createContentBlockStyles, interactionCard } from './styles/contentBlocks';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { ActionButton } from './ActionButton';
import { Dropdown, type DropdownOption } from './Dropdown';
import type { QuestionNarrationOutcomeHandler } from './narrationEvents';
import { RichTextContent } from './RichTextContent';

export interface CategorizationRendererProps {
  block: CategorizationBlock;
  onInteractionComplete?: () => void;
  onNarrationOutcomeCommitted?: QuestionNarrationOutcomeHandler;
  /**
   * NA-319: fires with the learner's REAL, actually-submitted assignments
   * (item id -> chosen category id) at the exact moment the block genuinely
   * completes (every item correct) — the same instant `onInteractionComplete`
   * fires. Unlike `initialCorrectAssignments` below, this is never inferred
   * or derived from any other signal; it's the live submission itself, safe
   * to remember verbatim. Categorization has no server-side persistence, so
   * `CourseCanvas.tsx` fingerprints this and hands the caller an opaque,
   * content-bound fingerprint string rather than the raw assignments — small
   * enough to also back up into the SCORM `suspend_data` envelope so a
   * genuinely correct answer survives an actual relaunch, not just a
   * same-session lesson-navigation remount.
   */
  onAssignmentsSubmitted?: (assignments: Record<string, string>) => void;
  /**
   * NA-319 relaunch/session-nav restore: the learner's guaranteed-correct
   * assignment (item id -> its own `correctCategoryId`), already verified
   * by the caller before this renders. `CourseCanvas.tsx` only ever supplies
   * this after confirming either (a) a restored content fingerprint still
   * matches this block's CURRENT `correctCategoryId` data, or (b) the
   * block's own lesson is confirmed complete and this block type
   * unconditionally gates lesson progression — so trusted here without
   * re-verification. When present, the block mounts directly into its
   * locked "complete" view instead of resetting to blank/interactive.
   */
  initialCorrectAssignments?: Record<string, string>;
}

type Phase = 'interactive' | 'submitted' | 'complete';
type ItemResult = 'correct' | 'incorrect';

const POOL_ID = '__pool__';
const CONTENT_CLASS = 'categorization-rich-content';

// AA-175 / NA-231: at and below this width the drag-to-categorize interaction
// is replaced by the dropdown-per-item mode — a second input surface over the
// same state machine, not a restyle. Matches the Single Choice layout gate.
const CATEGORIZATION_MOBILE_LAYOUT_BREAKPOINT = 768;

// Row height for the mobile item/dropdown pair, from the AA-175 frames.
const MOBILE_ROW_HEIGHT_PX = 50;

// ── Icon ────────────────────────────────────────────────────────────────────

function IconDisplay({
  icon,
  emojiSize = '24px',
}: {
  icon: CategorizationIcon;
  emojiSize?: string;
}) {
  if (icon.type === 'emoji') {
    return (
      <span style={{ fontSize: emojiSize, lineHeight: 1, flexShrink: 0 }}>
        {icon.value}
      </span>
    );
  }
  return (
    <img
      src={icon.value}
      alt=""
      style={{
        width: '24px',
        height: '24px',
        objectFit: 'contain',
        flexShrink: 0,
        borderRadius: '2px',
      }}
    />
  );
}

// ── Pool drop target (wrapper) ──────────────────────────────────────────────

function PoolDropZone({
  children,
  style,
}: {
  children: React.ReactNode;
  style: CSSProperties;
}) {
  const { setNodeRef } = useDroppable({ id: POOL_ID });
  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
}

// ── Draggable item pill (owns its own wrapper + optional picker) ───────────

function DraggableItem({
  item,
  isLocked,
  result,
  isSelected,
  onClick,
  baseStyle,
  picker,
  truncate = false,
}: {
  item: CategorizationItem;
  isLocked: boolean;
  result: ItemResult | null;
  isSelected: boolean;
  onClick: () => void;
  baseStyle: CSSProperties;
  picker?: React.ReactNode;
  /**
   * Placed-in-column variant: one line with ellipsis. The learner reads the
   * full label in the pool (where it wraps) before placing, so post-placement
   * truncation costs nothing.
   */
  truncate?: boolean;
}) {
  const { tokens } = useTheme();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: isLocked,
  });

  const transformStyle = transform
    ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
    : undefined;

  // State-driven overrides — order matters (terminal states win).
  const stateOverrides: CSSProperties = (() => {
    if (result === 'correct') {
      return {
        backgroundColor: tokens.choice.correct.bg,
        borderColor: tokens.choice.correct.border,
        boxShadow: 'none',
      };
    }
    if (result === 'incorrect') {
      return {
        backgroundColor: tokens.choice.wrong.bg,
        borderColor: tokens.choice.wrong.border,
        boxShadow: tokens.shadow.itemPill,
      };
    }
    if (isDragging) {
      return {
        backgroundColor: tokens.choice.drag.bg,
        borderWidth: '2px',
        borderColor: tokens.choice.drag.border,
        boxShadow: tokens.shadow.itemPillDrag,
      };
    }
    if (isSelected) {
      return {
        backgroundColor: tokens.choice.hover.bg,
        borderColor: tokens.choice.hover.border,
      };
    }
    return {};
  })();

  const interactive = !isLocked || result === 'incorrect';

  // Escalate the wrapper's stacking context so the dragged or picker-active
  // item floats above siblings. Without this, each wrapper's own
  // `position: relative` traps inner z-index changes inside its own context.
  const wrapperZIndex = isDragging ? 40 : isSelected ? 30 : 'auto';

  // Picker anchor flip — when the popover would overflow the viewport on
  // the right, anchor to the wrapper's right edge instead of left.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pickerAnchor, setPickerAnchor] = useState<'left' | 'right'>('left');

  useLayoutEffect(() => {
    if (!isSelected) {
      setPickerAnchor('left');
      return;
    }
    const wrapper = wrapperRef.current;
    const popover = popoverRef.current;
    if (!wrapper || !popover) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    const popoverWidth = popover.offsetWidth;
    const viewportWidth = window.innerWidth;
    const margin = 16;
    const overflowsRight = wrapperRect.left + popoverWidth + margin > viewportWidth;
    const fitsLeft = wrapperRect.right - popoverWidth >= margin;
    setPickerAnchor(overflowsRight && fitsLeft ? 'right' : 'left');
  }, [isSelected]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        zIndex: wrapperZIndex,
        // Width inheritance: keep the wrapper sized to its content in the pool
        // and to 100% in a category column (driven by baseStyle.width).
        width: baseStyle.width,
      }}
    >
      <div
        ref={setNodeRef}
        {...(interactive ? listeners : {})}
        {...(interactive ? attributes : {})}
        role="button"
        tabIndex={interactive ? 0 : -1}
        aria-label={`${item.label}${result ? `, ${result}` : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (interactive) onClick();
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && interactive) {
            e.preventDefault();
            onClick();
          }
        }}
        style={{
          ...baseStyle,
          ...stateOverrides,
          transform: transformStyle,
          cursor: !interactive ? 'default' : isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        {item.icon && <IconDisplay icon={item.icon} />}
        <span
          style={
            truncate
              ? {
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              : undefined
          }
        >
          {item.label}
        </span>
      </div>
      {picker && (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            ...(pickerAnchor === 'left' ? { left: 0 } : { right: 0 }),
            zIndex: 20,
          }}
        >
          {picker}
        </div>
      )}
    </div>
  );
}

// ── "Move to" popover picker ────────────────────────────────────────────────

function MoveToPicker({
  categories,
  onSelect,
  containerStyle,
  chipStyle,
  chipHoverStyle,
  labelStyle,
}: {
  categories: CategorizationCategory[];
  onSelect: (categoryId: string) => void;
  containerStyle: CSSProperties;
  chipStyle: CSSProperties;
  chipHoverStyle: CSSProperties;
  labelStyle: CSSProperties;
}) {
  const { t } = useTranslation();
  const [hoveredChipId, setHoveredChipId] = useState<string | null>(null);
  return (
    <div style={containerStyle} onClick={(e) => e.stopPropagation()} role="presentation">
      <span style={labelStyle}>{t('categorization.move_to')}</span>
      {categories.map((cat) => {
        const isHovered = hoveredChipId === cat.id;
        return (
          <button
            key={cat.id}
            type="button"
            onMouseEnter={() => setHoveredChipId(cat.id)}
            onMouseLeave={() => setHoveredChipId((prev) => (prev === cat.id ? null : prev))}
            onFocus={() => setHoveredChipId(cat.id)}
            onBlur={() => setHoveredChipId((prev) => (prev === cat.id ? null : prev))}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(cat.id);
            }}
            style={{
              ...chipStyle,
              ...(isHovered ? chipHoverStyle : {}),
            }}
          >
            {cat.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Category column with drag-target highlighting ──────────────────────────

function CategoryColumn({
  category,
  children,
  styles,
  cornerRadiusPx,
}: {
  category: CategorizationCategory;
  children: React.ReactNode;
  styles: {
    column: CSSProperties;
    columnOver: CSSProperties;
    header: CSSProperties;
    headerOver: CSSProperties;
    body: CSSProperties;
  };
  cornerRadiusPx: number;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: category.id });
  const { tokens } = useTheme();

  // Outline is drawn by an inline SVG so dash length is tunable.
  // `border-style: dashed` exposes no dash-length control.
  const strokeColor = isOver ? tokens.choice.hover.border : tokens.borders.default;
  const strokeWidth = isOver ? 1 : 2;
  const dashArray = isOver ? undefined : '2 2.5';
  const inset = strokeWidth / 2;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...styles.column,
        ...(isOver ? styles.columnOver : {}),
      }}
    >
      <svg
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        <rect
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={dashArray}
          rx={cornerRadiusPx}
          ry={cornerRadiusPx}
          style={{
            width: `calc(100% - ${strokeWidth}px)`,
            height: `calc(100% - ${strokeWidth}px)`,
            x: `${inset}px`,
            y: `${inset}px`,
          }}
        />
      </svg>
      <div
        style={{
          ...styles.header,
          ...(isOver ? styles.headerOver : {}),
          position: 'relative',
        }}
      >
        {category.icon && <IconDisplay icon={category.icon} emojiSize="20px" />}
        <span
          style={{
            // Wraps rather than truncates — the header is the only place the
            // learner can read the full category name.
            flex: 1,
            minWidth: 0,
            overflowWrap: 'break-word',
          }}
        >
          {category.label}
        </span>
      </div>
      <div style={{ ...styles.body, position: 'relative' }}>{children}</div>
    </div>
  );
}

// ── Main renderer ───────────────────────────────────────────────────────────

export function CategorizationRenderer({
  block,
  onInteractionComplete,
  onNarrationOutcomeCommitted,
  onAssignmentsSubmitted,
  initialCorrectAssignments,
}: CategorizationRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const isMobileLayout = useIsMobileViewport(CATEGORIZATION_MOBILE_LAYOUT_BREAKPOINT);
  const interactionWidth = getStandardInteractionWidth(tokens, isMobile);

  // Rich text rendering
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const resetStyles = useMemo(
    () => createRichTextContentResetStyles(CONTENT_CLASS),
    []
  );

  const descriptionHtml = useMemo(() => {
    if (!block.description) return '';
    /**
     * NA-382: must distinguish "no question text" from "question text
     * deliberately overridden to blank" — the latter is a truthy doc holding an
     * empty paragraph, and plain generation turns it into '<p></p>', which
     * renders as a visible empty block. Same guard NA-313 added for the
     * explanation; Likert and Workplace Color Spectrum already had it.
     */
    return safeGenerateNonEmptyRichTextHTML(block.description, extensions);
  }, [block.description, extensions]);

  const correctFeedbackHtml = useMemo(() => {
    if (!block.correctFeedbackBody) return '';
    return safeGenerateRichTextHTML(block.correctFeedbackBody, extensions);
  }, [block.correctFeedbackBody, extensions]);

  const incorrectFeedbackHtml = useMemo(() => {
    if (!block.incorrectFeedbackBody) return '';
    return safeGenerateRichTextHTML(block.incorrectFeedbackBody, extensions);
  }, [block.incorrectFeedbackBody, extensions]);

  // Randomized item order — stable across re-renders while block.items is
  // unchanged; recomputed when items are added/removed/edited (live authoring).
  const randomizedItems = useMemo<CategorizationItem[]>(
    () => [...block.items].sort(() => Math.random() - 0.5),
    [block.items]
  );

  // State
  const [assignments, setAssignments] = useState<Record<string, string>>(() => {
    if (initialCorrectAssignments) return initialCorrectAssignments;
    const initial: Record<string, string> = {};
    for (const item of block.items) {
      initial[item.id] = POOL_ID;
    }
    return initial;
  });

  // Reconcile assignments when the item-id set changes (live authoring edits):
  // new items start in the pool, removed items are dropped, existing
  // assignments are preserved.
  useEffect(() => {
    setAssignments((prev) => {
      const next: Record<string, string> = {};
      let changed = false;
      for (const item of block.items) {
        const existing = prev[item.id];
        if (existing === undefined) {
          next[item.id] = POOL_ID;
          changed = true;
        } else {
          next[item.id] = existing;
        }
      }
      if (!changed && Object.keys(prev).length === block.items.length) {
        return prev;
      }
      return next;
    });
  }, [block.items]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>(initialCorrectAssignments ? 'complete' : 'interactive');
  const [lockedItemIds, setLockedItemIds] = useState<Set<string>>(
    () => new Set(initialCorrectAssignments ? block.items.map((item) => item.id) : [])
  );
  const [results, setResults] = useState<Record<string, ItemResult> | null>(() => {
    if (!initialCorrectAssignments) return null;
    const allCorrect: Record<string, ItemResult> = {};
    for (const item of block.items) allCorrect[item.id] = 'correct';
    return allCorrect;
  });

  // NA-319: `initialCorrectAssignments` is derived from the player's
  // block-completion tracking, which can resolve asynchronously after this
  // block's first mount (the same is true of the bootstrap fetch driving
  // Likert/Single-Choice/WCS restore). The `phase !== 'interactive'` guard
  // avoids clobbering an answer already in progress from a live submission.
  useEffect(() => {
    if (!initialCorrectAssignments || phase !== 'interactive') return;
    setAssignments(initialCorrectAssignments);
    setPhase('complete');
    setLockedItemIds(new Set(block.items.map((item) => item.id)));
    const allCorrect: Record<string, ItemResult> = {};
    for (const item of block.items) allCorrect[item.id] = 'correct';
    setResults(allCorrect);
  }, [initialCorrectAssignments, phase, block.items]);
  useCommittedQuestionNarrationOutcome({
    outcomeKey: phase === 'interactive' ? null : phase,
    outcome: phase === 'interactive' ? null : (phase === 'complete' ? 'correct' : 'incorrect'),
    presentation: phase === 'interactive' ? null : 'feedback',
    onCommitted: onNarrationOutcomeCommitted,
    reset: phase === 'interactive',
  });

  const sharedStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const sensors = useDefaultDndSensors();

  // Derived
  const poolItems = useMemo(
    () => randomizedItems.filter((item) => assignments[item.id] === POOL_ID),
    [assignments, randomizedItems]
  );

  const categoryItems = useCallback(
    (categoryId: string) =>
      randomizedItems.filter((item) => assignments[item.id] === categoryId),
    [assignments, randomizedItems]
  );

  // An item counts as assigned only when its entry exists AND is not the pool —
  // items not yet reconciled into `assignments` must not count as assigned.
  const allAssigned = useMemo(
    () =>
      block.items.every((item) => {
        const assignment = assignments[item.id];
        return assignment !== undefined && assignment !== POOL_ID;
      }),
    [assignments, block.items]
  );

  // Clear result for a moved item; reset to interactive once no incorrect items remain.
  const clearResultAndMaybeReset = useCallback(
    (itemId: string) => {
      if (!results || !results[itemId]) return;
      const next = { ...results };
      delete next[itemId];
      const hasIncorrect = Object.values(next).some((r) => r === 'incorrect');
      if (hasIncorrect || phase !== 'submitted') {
        setResults(Object.keys(next).length > 0 ? next : null);
        return;
      }
      // No incorrect items remain — return to interactive, keeping only the
      // results of locked (correct) items.
      const kept: Record<string, ItemResult> = {};
      for (const [id, result] of Object.entries(next)) {
        if (lockedItemIds.has(id)) kept[id] = result;
      }
      setResults(Object.keys(kept).length > 0 ? kept : null);
      setPhase('interactive');
    },
    [results, phase, lockedItemIds]
  );

  // Handlers
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const itemId = active.id as string;
      if (lockedItemIds.has(itemId)) return;
      const targetId = over.id as string;
      const validTargets = [POOL_ID, ...block.categories.map((c) => c.id)];
      if (!validTargets.includes(targetId)) return;
      setAssignments((prev) => ({ ...prev, [itemId]: targetId }));
      setSelectedItemId(null);
      clearResultAndMaybeReset(itemId);
    },
    [block.categories, lockedItemIds, clearResultAndMaybeReset]
  );

  const handleItemClick = useCallback((itemId: string) => {
    setSelectedItemId((prev) => (prev === itemId ? null : itemId));
  }, []);

  const handleMoveToCategory = useCallback(
    (categoryId: string) => {
      if (!selectedItemId) return;
      setAssignments((prev) => ({ ...prev, [selectedItemId]: categoryId }));
      clearResultAndMaybeReset(selectedItemId);
      setSelectedItemId(null);
    },
    [selectedItemId, clearResultAndMaybeReset]
  );

  // Mobile dropdown mode — a category pick is the same state transition as a
  // drop: assign, then clear any stale result for the item.
  const handleMobileSelect = useCallback(
    (itemId: string, categoryId: string) => {
      setAssignments((prev) => ({ ...prev, [itemId]: categoryId }));
      clearResultAndMaybeReset(itemId);
    },
    [clearResultAndMaybeReset]
  );

  const handleSubmit = useCallback(() => {
    if (!allAssigned) return;
    const newResults: Record<string, ItemResult> = {};
    let allCorrect = true;
    for (const item of block.items) {
      const assignedCategoryId = assignments[item.id];
      if (assignedCategoryId === item.correctCategoryId) {
        newResults[item.id] = 'correct';
      } else {
        newResults[item.id] = 'incorrect';
        allCorrect = false;
      }
    }
    setResults(newResults);
    const newLocked = new Set(lockedItemIds);
    for (const item of block.items) {
      if (newResults[item.id] === 'correct') newLocked.add(item.id);
    }
    setLockedItemIds(newLocked);
    if (allCorrect) {
      setPhase('complete');
      onInteractionComplete?.();
      // NA-319: the real, actually-submitted assignments — not inferred
      // from any other signal — so the caller can safely remember this
      // exact block's answer for the rest of the browser session.
      onAssignmentsSubmitted?.(assignments);
    } else {
      setPhase('submitted');
    }
  }, [allAssigned, assignments, block.items, lockedItemIds, onInteractionComplete, onAssignmentsSubmitted]);

  const handleTryAgain = useCallback(() => {
    setSelectedItemId(null);
    const newAssignments = { ...assignments };
    for (const item of block.items) {
      if (!lockedItemIds.has(item.id) && results?.[item.id] === 'incorrect') {
        newAssignments[item.id] = POOL_ID;
      }
    }
    setAssignments(newAssignments);
    const keptResults: Record<string, ItemResult> = {};
    for (const item of block.items) {
      if (lockedItemIds.has(item.id)) keptResults[item.id] = 'correct';
    }
    setResults(Object.keys(keptResults).length > 0 ? keptResults : null);
    setPhase('interactive');
  }, [assignments, block.items, lockedItemIds, results]);

  const handleDismissOverlay = useCallback(() => {
    setSelectedItemId(null);
  }, []);

  // ── Styles ────────────────────────────────────────────────────────────────

  // NA-227/NA-231: full three-tier content-block heading stepping.
  const titleTypography = useMemo(
    () =>
      getContentBlockHeadingTypography(tokens, block.titleLevel ?? 'h2', {
        isMobile,
        isSmallMobile,
      }),
    [block.titleLevel, isMobile, isSmallMobile, tokens],
  );

  const s = useMemo(() => {
    // NA-231: interactive text (item labels, dropdown values) steps
    // 20 → 18 (<768) → 16 (≤576), matching the Single Choice convention.
    const interactiveTextSize = isSmallMobile
      ? tokens.typography.mobile.body.size
      : isMobileLayout
        ? tokens.typography.sizes.ui
        : tokens.typography.body.size;

    // Shared item-pill base — Default state per AA-147.
    // Longhand border properties so DnD transforms don't fight the shorthand.
    const itemPillBase: CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      padding: `10px ${tokens.spacing.lg}`,
      backgroundColor: tokens.backgrounds.app,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.interactive,
      borderRadius: tokens.radius.xs,
      boxShadow: tokens.shadow.itemPill,
      fontSize: tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.normal,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      // In the pool, long labels wrap (the pill grows taller) so the learner
      // can read them in full before placing; maxWidth keeps a wrapping chip
      // inside the pool box. Placed pills re-pin to one line (itemChipFull).
      whiteSpace: 'normal',
      maxWidth: '100%',
      boxSizing: 'border-box',
      transition: 'background-color 150ms, border-color 150ms, box-shadow 150ms',
    };

    return {
      wrapper: {
        fontFamily: tokens.typography.fontFamily,
        color: tokens.text.primary,
        width: interactionWidth,
        maxWidth: '100%',
        margin: '0 auto',
      } as CSSProperties,
      title: {
        fontSize: titleTypography.size,
        fontWeight: titleTypography.weight,
        lineHeight: titleTypography.lineHeight,
        color: tokens.text.primary,
        margin: 0,
        marginBottom: tokens.spacing.xs,
      } as CSSProperties,
      description: {
        // NA-205/NA-227 convention: reading prose steps to 16px at ≤576.
        fontSize: isSmallMobile
          ? tokens.typography.mobile.body.size
          : tokens.typography.body.size,
        lineHeight: tokens.typography.body.lineHeight,
        color: tokens.text.primary,
        marginBottom: isMobileLayout ? tokens.spacing.md : tokens.spacing.lg,
      } as CSSProperties,

      // Outer card — bg #F1F1F1, 3px white border, 15px radius, shadow.lg.
      // No flex `gap` — child margins drive the asymmetric vertical rhythm.
      // Mobile padding (30/18) is from the AA-175 redlines.
      card: {
        ...interactionCard(tokens),
        padding: isMobileLayout ? '30px 18px' : `25px ${tokens.spacing.xl}`,
        position: 'relative',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      } as CSSProperties,

      // ── Mobile dropdown-per-item mode (AA-175 / NA-231) ────────────────────

      // Instruction line sits with the title block above the card on mobile
      // (desktop keeps its pool instruction inside the card).
      mobileInstruction: {
        fontSize: isSmallMobile ? tokens.typography.sizes.sm : tokens.typography.sizes.ui,
        fontStyle: 'italic',
        fontWeight: tokens.typography.weights.regular,
        lineHeight: tokens.typography.lineHeights.normal,
        color: tokens.text.primary,
        margin: 0,
        marginBottom: tokens.spacing.xl,
      } as CSSProperties,

      mobileRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
      } as CSSProperties,
      mobileRow: {
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
      } as CSSProperties,

      // Left half of the joined pair — item label box. Rounded on the left
      // only; the dropdown trigger rounds the right and overlaps the shared
      // border by 1px so the pair reads as one outline.
      mobileLabelBox: {
        // 55/45 split with the dropdown: labels can run long and wrap;
        // the dropdown only holds a short category name.
        flex: '1 1 55%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing.sm,
        // minHeight, not height: long labels wrap on mobile (no pool to read
        // them in first), growing the row; the dropdown stretches to match.
        minHeight: `${MOBILE_ROW_HEIGHT_PX}px`,
        padding: '4px 14px',
        backgroundColor: tokens.backgrounds.app,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: tokens.borders.interactive,
        borderRadius: `${tokens.radius.xs} 0 0 ${tokens.radius.xs}`,
        boxSizing: 'border-box',
        transition: 'background-color 150ms, border-color 150ms',
      } as CSSProperties,
      mobileLabelText: {
        fontSize: interactiveTextSize,
        fontWeight: tokens.typography.weights.bold,
        // Same ruling as the desktop category headers: wrapped labels read
        // too airy at `normal`.
        lineHeight: tokens.typography.lineHeights.tight,
        fontFamily: tokens.typography.fontFamily,
        color: tokens.text.primary,
        flex: 1,
        minWidth: 0,
        overflowWrap: 'break-word',
      } as CSSProperties,

      // Trigger overrides for the shared Dropdown — right half of the pair.
      // height 100% (with the wrapper stretching) keeps the trigger flush
      // with a label box that has grown from a wrapped label.
      mobileDropdownTrigger: {
        minHeight: `${MOBILE_ROW_HEIGHT_PX}px`,
        height: '100%',
        padding: `0 ${tokens.spacing.sm} 0 14px`,
        fontSize: interactiveTextSize,
        fontWeight: tokens.typography.weights.regular,
        borderColor: tokens.borders.interactive,
        borderRadius: `0 ${tokens.radius.xs} ${tokens.radius.xs} 0`,
        marginLeft: '-1px',
        transition: 'background-color 150ms, border-color 150ms',
      } as CSSProperties,

      // Italic instruction shown above the bordered pool box.
      // Tight label→input gap below.
      poolInstruction: {
        fontSize: tokens.typography.sizes.ui,
        fontStyle: 'italic',
        fontWeight: tokens.typography.weights.regular,
        lineHeight: tokens.typography.lineHeights.normal,
        color: tokens.text.primary,
        margin: 0,
        marginBottom: tokens.spacing.sm,
      } as CSSProperties,

      // "Selections box" — 1px solid #CACACA, 5px radius, transparent
      // (inherits the outer card's #F1F1F1 fill). Bottom spacing is owned by
      // the pool-group wrapper so it can collapse alongside the height
      // animation when the activity completes.
      // display: grid stacks the visible items over a hidden ghost copy of
      // the full item set (both in cell 1/1): the ghost keeps the box sized
      // for all items at the current width — it can't shrink as items are
      // placed, yet reflows when the viewport changes.
      poolContainer: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: tokens.borders.default,
        borderRadius: tokens.radius.xs,
        backgroundColor: 'transparent',
        padding: '13px 15px 16px',
        minHeight: '60px',
        boxSizing: 'border-box',
        display: 'grid',
      } as CSSProperties,
      poolItems: {
        display: 'flex',
        flexWrap: 'wrap',
        rowGap: '9px',
        columnGap: '13px',
        // The layer stretches to the ghost-defined cell height; without this,
        // wrapped flex lines spread out to fill it (align-content: stretch).
        alignContent: 'flex-start',
      } as CSSProperties,

      categoriesGrid: {
        display: 'grid',
        // `minmax(0, 1fr)` forces equal column widths even when an item
        // has long content; pairs with `overflow: hidden` on the placed
        // item pill to truncate with ellipsis.
        gridTemplateColumns: `repeat(${block.categories.length}, minmax(0, 1fr))`,
        // Shared header/body rows: every header takes the tallest header's
        // height (via subgrid on the columns) so the divider lines align
        // even when one label wraps. Both rows must be `auto` — a spanning
        // item's minHeight is ignored by flexible (fr) tracks, so `1fr`
        // collapses the body row.
        gridTemplateRows: 'auto auto',
        // Column gap only — a row gap would open a seam between each
        // column's header and body (subgrid inherits the parent's gutters).
        columnGap: tokens.spacing.xl,
        rowGap: 0,
      } as CSSProperties,

      // Category column — Default: white bg + dashed outline (drawn by SVG
      // overlay inside CategoryColumn so dash length is tunable).
      // Drag-over: blue bg, outline switches to solid blue (also via SVG).
      categoryColumn: {
        position: 'relative',
        borderRadius: tokens.radius.xs,
        backgroundColor: tokens.backgrounds.app,
        // Subgrid shares the parent's header/body rows across all columns,
        // keeping header dividers aligned when a label wraps. The single
        // `minmax(0, 1fr)` column track stretches children to the column
        // width and lets long nowrap labels shrink into their ellipsis
        // (an auto track would grow past the column instead). The old
        // column-level minHeight moved to the body (see categoryBody) —
        // spanning items can't impose it through subgrid rows.
        display: 'grid',
        gridRow: 'span 2',
        gridTemplateRows: 'subgrid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        boxSizing: 'border-box',
        transition: 'background-color 150ms',
      } as CSSProperties,
      categoryColumnOver: {
        backgroundColor: tokens.choice.hover.bg,
      } as CSSProperties,
      categoryHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing.md,
        padding: `${tokens.spacing.md} ${tokens.spacing.lgPlus} 6px`,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.borders.default,
        fontSize: tokens.typography.body.size,
        fontWeight: tokens.typography.weights.bold,
        // Wrapped header labels read too airy at `normal` — tighten.
        lineHeight: tokens.typography.lineHeights.tight,
        color: tokens.text.primary,
        boxSizing: 'border-box',
        transition: 'border-bottom-color 150ms',
      } as CSSProperties,
      categoryHeaderOver: {
        borderBottomColor: tokens.choice.hover.border,
      } as CSSProperties,
      categoryBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        padding: `${tokens.spacing.md} ${tokens.spacing.lgPlus} 21px`,
        // Drop-target breathing room — replaces the old 260px column
        // minHeight (~50px single-line header + ~210px body).
        minHeight: '210px',
        boxSizing: 'border-box',
      } as CSSProperties,

      // Item pill variants
      itemChip: itemPillBase,
      itemChipFull: {
        ...itemPillBase,
        display: 'flex',
        width: '100%',
        // Placed pills stay single-line with ellipsis (the full label was
        // readable in the pool). overflow: hidden engages the span ellipsis.
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      } as CSSProperties,

      // Move-to popover visual styling. Positioning (absolute + anchor) is
      // owned by DraggableItem, which can flip the anchor based on viewport.
      // `width: max-content` lets the popover size to its chips and extend
      // past the item wrapper without wrapping vertically.
      pickerPopover: {
        width: 'max-content',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: tokens.spacing.sm,
        padding: '10px',
        backgroundColor: tokens.backgrounds.app,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: tokens.borders.interactive,
        borderRadius: tokens.radius.xs,
        boxShadow: tokens.shadow.itemPill,
        boxSizing: 'border-box',
      } as CSSProperties,
      pickerLabel: {
        fontSize: tokens.typography.sizes.sm,
        fontWeight: tokens.typography.weights.regular,
        color: tokens.text.primary,
        marginRight: tokens.spacing.xs,
        whiteSpace: 'nowrap',
      } as CSSProperties,
      pickerChip: {
        padding: '5px 15px',
        fontSize: tokens.typography.sizes.sm,
        fontWeight: tokens.typography.weights.regular,
        fontFamily: tokens.typography.fontFamily,
        lineHeight: tokens.typography.lineHeights.normal,
        backgroundColor: tokens.backgrounds.app,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: tokens.borders.default,
        borderRadius: tokens.radius.xs,
        color: tokens.text.primary,
        cursor: 'pointer',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        transition: 'background-color 150ms, border-color 150ms',
      } as CSSProperties,
      pickerChipHover: {
        borderColor: tokens.choice.hover.border,
        backgroundColor: tokens.choice.hover.bg,
      } as CSSProperties,
      // Backdrop dim — spec: rgba(0,0,0,0.20)
      overlay: {
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.20)',
        borderRadius: tokens.radius.xl,
        zIndex: 5,
        cursor: 'pointer',
      } as CSSProperties,

      // Submit / Try Again button (extends shared actionButton)
      submitButton: {
        ...sharedStyles.actionButton,
        display: 'block',
        margin: `${tokens.spacing.xl} auto 0`,
        transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
      } as CSSProperties,

      // Feedback (Perfect! / Not quite right) — rendered BELOW the card,
      // between card and submit/try-again button. Top margin matches the
      // Figma 20px gap above feedback; the button's own marginTop produces
      // the matching gap below.
      feedbackArea: {
        textAlign: 'center',
        marginTop: tokens.spacing.lgPlus,
      } as CSSProperties,
      feedbackHeading: {
        // AA-175 shows a 20px bold heading on the phone frames; 24 bridges
        // the 577–768 tier (mobile h3), desktop keeps 30.
        fontSize: isSmallMobile
          ? tokens.typography.sizes.md
          : isMobileLayout
            ? tokens.typography.sizes.lg
            : tokens.typography.sizes.h3,
        fontWeight: tokens.typography.weights.bold,
        lineHeight: tokens.typography.lineHeights.normal,
        color: tokens.text.primary,
        margin: 0,
        marginBottom: tokens.spacing.xs,
      } as CSSProperties,
      feedbackBody: {
        // Reading prose: 16px at ≤576, per the shared convention.
        fontSize: isSmallMobile
          ? tokens.typography.mobile.body.size
          : tokens.typography.body.size,
        fontWeight: tokens.typography.weights.regular,
        lineHeight: tokens.typography.lineHeights.normal,
        color: tokens.text.primary,
        margin: 0,
      } as CSSProperties,
    };
  }, [
    block.categories.length,
    interactionWidth,
    isMobileLayout,
    isSmallMobile,
    titleTypography,
    tokens,
    sharedStyles,
  ]);

  const submitDisabled = !allAssigned || phase === 'submitted' || phase === 'complete';
  const showPool = phase !== 'complete';
  // The drag copy would mislead in the dropdown mode, so each mode has its own
  // default; an authored instructionText wins in both.
  const poolInstruction =
    block.instructionText ??
    (isMobileLayout
      ? t('categorization.select_category_instruction')
      : t('categorization.assign_categories_instruction'));

  const mobileCategoryOptions: DropdownOption[] = block.categories.map((category) => ({
    value: category.id,
    label: category.label,
  }));

  const categoryColumnStyles = {
    column: s.categoryColumn,
    columnOver: s.categoryColumnOver,
    header: s.categoryHeader,
    headerOver: s.categoryHeaderOver,
    body: s.categoryBody,
  };

  return (
    <div style={s.wrapper}>
      <style>{resetStyles}</style>

      {/* Title */}
      {block.title && <div style={s.title}>{block.title}</div>}

      {/* Description (rich text) */}
      {descriptionHtml && (
        <RichTextContent
          className={CONTENT_CLASS}
          style={s.description}
          html={descriptionHtml}
        />
      )}

      {/* Mobile dropdown-per-item mode (AA-175): the instruction joins the
          title block above the card, and each item renders as a label box +
          category dropdown pair over the same assignment/phase state machine
          as desktop drag-and-drop. */}
      {isMobileLayout && (
        <>
          <p style={s.mobileInstruction}>{poolInstruction}</p>
          <div style={s.card}>
            <div style={s.mobileRows}>
              {randomizedItems.map((item) => {
                const itemResult = results?.[item.id] ?? null;
                const tint: CSSProperties =
                  itemResult === 'correct'
                    ? {
                        backgroundColor: tokens.choice.correct.bg,
                        borderColor: tokens.choice.correct.border,
                      }
                    : itemResult === 'incorrect'
                      ? {
                          backgroundColor: tokens.choice.wrong.bg,
                          borderColor: tokens.choice.wrong.border,
                        }
                      : {};
                const assignment = assignments[item.id];
                return (
                  <div key={item.id} style={s.mobileRow}>
                    <div style={{ ...s.mobileLabelBox, ...tint }}>
                      {item.icon && <IconDisplay icon={item.icon} />}
                      <span style={s.mobileLabelText}>{item.label}</span>
                    </div>
                    {/* display: flex so the Dropdown root stretches to the
                        row height when the label box grows from wrapping. */}
                    <div style={{ flex: '1 1 45%', minWidth: 0, display: 'flex' }}>
                      <Dropdown
                        value={
                          assignment === undefined || assignment === POOL_ID
                            ? ''
                            : assignment
                        }
                        options={mobileCategoryOptions}
                        onChange={(categoryId) => handleMobileSelect(item.id, categoryId)}
                        placeholder={t('categorization.select')}
                        disabled={phase !== 'interactive' || lockedItemIds.has(item.id)}
                        ariaLabel={t('categorization.category_for_item', { item: item.label })}
                        triggerStyle={{ ...s.mobileDropdownTrigger, ...tint }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Outer card — desktop drag-and-drop mode */}
      {!isMobileLayout && (
      <div style={s.card}>
        {/* Backdrop dim while picker is open */}
        {selectedItemId && (
          <div style={s.overlay} onClick={handleDismissOverlay} role="presentation" />
        )}

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          {/* Pool — always rendered so the collapse transition has a
              from-state. `grid-template-rows` 1fr → 0fr is animatable;
              `overflow: visible` while expanded so item picker popovers
              don't get clipped, then flips to `hidden` during collapse. */}
          <div
            aria-hidden={!showPool}
            style={{
              display: 'grid',
              gridTemplateRows: showPool ? '1fr' : '0fr',
              overflow: showPool ? 'visible' : 'hidden',
              marginBottom: showPool ? tokens.spacing.xl : 0,
              opacity: showPool ? 1 : 0,
              transition:
                'grid-template-rows 300ms ease-out, margin-bottom 300ms ease-out, opacity 200ms ease-out',
            }}
          >
            <div style={{ minHeight: 0 }}>
              <p style={s.poolInstruction}>{poolInstruction}</p>
              <PoolDropZone style={s.poolContainer}>
                  {/* Ghost of the full item set — invisible, but keeps the
                      box tall enough for every item at the current width. */}
                  <div
                    aria-hidden
                    style={{ ...s.poolItems, gridArea: '1 / 1', visibility: 'hidden' }}
                  >
                    {randomizedItems.map((item) => (
                      <div key={item.id} style={s.itemChip}>
                        {item.icon && <IconDisplay icon={item.icon} />}
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ ...s.poolItems, gridArea: '1 / 1' }}>
                    {poolItems.map((item) => {
                      const isItemSelected = selectedItemId === item.id;
                      return (
                        <DraggableItem
                          key={item.id}
                          item={item}
                          isLocked={lockedItemIds.has(item.id)}
                          result={results?.[item.id] ?? null}
                          isSelected={isItemSelected}
                          onClick={() => handleItemClick(item.id)}
                          baseStyle={s.itemChip}
                          picker={
                            isItemSelected ? (
                              <MoveToPicker
                                categories={block.categories}
                                onSelect={handleMoveToCategory}
                                containerStyle={s.pickerPopover}
                                chipStyle={s.pickerChip}
                                chipHoverStyle={s.pickerChipHover}
                                labelStyle={s.pickerLabel}
                              />
                            ) : null
                          }
                        />
                      );
                    })}
                  </div>
              </PoolDropZone>
            </div>
          </div>

          {/* Categories */}
          <div style={s.categoriesGrid}>
            {block.categories.map((category) => {
              const items = categoryItems(category.id);
              return (
                <CategoryColumn
                  key={category.id}
                  category={category}
                  styles={categoryColumnStyles}
                  cornerRadiusPx={4}
                >
                  {items.map((item) => {
                    const itemResult = results?.[item.id] ?? null;
                    const isItemSelected = selectedItemId === item.id;
                    // Clickable for any non-locked item. During 'submitted',
                    // a non-locked item is either still 'incorrect' or had its
                    // result cleared by a mid-phase move — both are valid to
                    // reposition.
                    const isClickable =
                      phase === 'interactive' ||
                      (phase === 'submitted' && !lockedItemIds.has(item.id));
                    return (
                      <DraggableItem
                        key={item.id}
                        item={item}
                        isLocked={lockedItemIds.has(item.id)}
                        result={itemResult}
                        isSelected={isItemSelected}
                        onClick={() => {
                          if (isClickable) handleItemClick(item.id);
                        }}
                        baseStyle={s.itemChipFull}
                        truncate
                        picker={
                          isItemSelected ? (
                            <MoveToPicker
                              categories={block.categories.filter(
                                (c) => c.id !== assignments[item.id]
                              )}
                              onSelect={handleMoveToCategory}
                              containerStyle={s.pickerPopover}
                              chipStyle={s.pickerChip}
                              chipHoverStyle={s.pickerChipHover}
                              labelStyle={s.pickerLabel}
                            />
                          ) : null
                        }
                      />
                    );
                  })}
                </CategoryColumn>
              );
            })}
          </div>
        </DndContext>
      </div>
      )}

      {/* Feedback — rendered below the card per AA-147. */}
      {phase === 'submitted' && (
        <div style={s.feedbackArea}>
          <div style={s.feedbackHeading}>
            {block.incorrectFeedbackHeading ?? t('categorization.not_quite_right')}
          </div>
          {incorrectFeedbackHtml ? (
            <RichTextContent
              className={CONTENT_CLASS}
              style={s.feedbackBody}
              html={incorrectFeedbackHtml}
            />
          ) : null}
        </div>
      )}

      {phase === 'complete' && (
        <div style={s.feedbackArea}>
          <div style={s.feedbackHeading}>
            {block.correctFeedbackHeading ?? t('categorization.perfect')}
          </div>
          {correctFeedbackHtml ? (
            <RichTextContent
              className={CONTENT_CLASS}
              style={s.feedbackBody}
              html={correctFeedbackHtml}
            />
          ) : null}
        </div>
      )}

      {/* Submit / Try again button */}
      {phase === 'interactive' && (
        <ActionButton
          disabled={submitDisabled}
          onClick={handleSubmit}
          baseStyle={s.submitButton}
          disabledStyle={sharedStyles.actionButtonDisabled}
          hoverStyle={sharedStyles.actionButtonHover}
        >
          {block.submitButtonLabel ?? t('categorization.check_answers')}
        </ActionButton>
      )}

      {phase === 'submitted' && (
        <ActionButton
          onClick={handleTryAgain}
          baseStyle={s.submitButton}
          hoverStyle={sharedStyles.actionButtonHover}
        >
          {t('categorization.try_again')}
        </ActionButton>
      )}
    </div>
  );
}

CategorizationRenderer.displayName = 'CategorizationRenderer';
